// Finance helpers shared by the expense list, detail sheet and form page.
// Amounts are handled as integer minor units (cents, or whole francs for XOF), never as floats:
// the server stores and compares them the same way (backend/finance/rules.py).
import { errorMessage } from "./api";

export const EXPONENTS = { EUR: 2, XOF: 0, USD: 2, GBP: 2, CAD: 2, CHF: 2 };

export const WORKFLOW_LABELS = { draft: "Brouillon", submitted: "Soumise" };
export const PAYMENT_LABELS = { unpaid: "Non payée", partially_paid: "Partiellement payée", paid: "Payée" };

// "1 234,50" / "1234.5" / "1 234" → integer minor units, or null when not a valid positive amount for that currency.
export function toMinor(text, currency) {
  const exponent = EXPONENTS[currency];
  if (exponent === undefined || text === null || text === undefined) return null;
  const clean = String(text).replace(/[\s  ]/g, "").replace(",", ".");
  const match = /^(\d+)(?:\.(\d*))?$/.exec(clean);
  if (!match) return null;
  const decimals = (match[2] || "").replace(/0+$/, "");
  if (decimals.length > exponent) return null;
  const minor = Number(match[1] + decimals.padEnd(exponent, "0"));
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

// Minor units → plain decimal string the API accepts ("12.30", "600000").
export function minorToText(minor, currency) {
  const exponent = EXPONENTS[currency] ?? 2;
  if (!exponent) return String(minor);
  const digits = String(Math.abs(minor)).padStart(exponent + 1, "0");
  return `${minor < 0 ? "-" : ""}${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`;
}

export function formatMinor(minor, currency) {
  const exponent = EXPONENTS[currency] ?? 2;
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency, minimumFractionDigits: exponent, maximumFractionDigits: exponent })
      .format(minor / 10 ** exponent);
  } catch {
    return `${minorToText(minor, currency)} ${currency}`;
  }
}

// API amounts are decimal strings ("600000", "12.34").
export function formatAmount(text, currency) {
  const minor = toMinor(text, currency);
  // toMinor accepts only positive amounts; a zero total (« 0.00 ») must still read like any other amount.
  if (minor === null && /^0+(?:[.,]0+)?$/.test(String(text ?? "0").trim())) return formatMinor(0, currency);
  return minor === null ? `${text ?? "0"} ${currency}` : formatMinor(minor, currency);
}

export function formatDay(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const normalizeName = (value) => (value || "").trim().split(/\s+/).join(" ").toLocaleLowerCase("fr");

// One payer per distribution: Jamora once, each collaborator once, each third party (by normalized name) once.
export function payerKey(funding) {
  if (funding.payer_type === "company") return "company";
  if (funding.payer_type === "collaborator") return funding.collaborator_id ? `collaborator:${funding.collaborator_id}` : null;
  if (funding.payer_type === "third_party") return normalizeName(funding.third_party_name) ? `third_party:${normalizeName(funding.third_party_name)}` : null;
  return null;
}

function paymentStatus(totalMinor, paidMinor) {
  if (!paidMinor) return "unpaid";
  return paidMinor >= totalMinor ? "paid" : "partially_paid";
}

// Live totals and per-row problems for the "Qui a payé ?" section. Rows are only blocking once started.
export function evaluateFundings(fundings, amountText, currency, today = todayIso()) {
  const totalMinor = toMinor(amountText, currency);
  const rowErrors = fundings.map(() => ({}));
  // The same payer may appear on several lines: one per payment, each with its own date.
  let paidMinor = 0;
  fundings.forEach((f, i) => {
    const errors = rowErrors[i];
    if (!f.payer_type) errors.payer = "Choisissez qui a payé.";
    else if (f.payer_type === "collaborator" && !f.collaborator_id) errors.payer = "Choisissez le collaborateur.";
    else if (f.payer_type === "third_party" && !normalizeName(f.third_party_name)) errors.payer = "Indiquez le nom de la personne ou de l'organisation.";
    const minor = toMinor(f.amount, currency);
    if (minor === null) errors.amount = String(f.amount || "").trim() ? "Montant invalide." : "Indiquez le montant payé.";
    else paidMinor += minor;
    if (!f.paid_at) errors.paid_at = "Indiquez la date du paiement.";
    else if (f.paid_at > today) errors.paid_at = "Un paiement ne peut pas être daté dans le futur.";
  });
  const overpaid = totalMinor !== null && paidMinor > totalMinor;
  const remainingMinor = totalMinor === null ? null : Math.max(totalMinor - paidMinor, 0);
  return {
    totalMinor, paidMinor, remainingMinor, overpaid, rowErrors,
    status: totalMinor === null ? (paidMinor ? "partially_paid" : "unpaid") : paymentStatus(totalMinor, paidMinor),
    valid: !overpaid && rowErrors.every((e) => !Object.keys(e).length),
  };
}

export const emptyFunding = (amount = "") => ({
  payer_type: "", collaborator_id: null, collaborator_label: "", third_party_name: "", amount, paid_at: "", note: "",
});

export function fundingPayload(f) {
  return {
    payer_type: f.payer_type,
    collaborator_id: f.payer_type === "collaborator" ? f.collaborator_id : null,
    third_party_name: f.payer_type === "third_party" ? f.third_party_name.trim() : null,
    amount: f.amount, paid_at: f.paid_at || null, note: f.note || null,
  };
}

// Payments that count: a cancelled one stays on the expense for its history but never counts.
export const activeFundings = (expense) => (expense?.fundings || []).filter((f) => !f.cancelled_at);

// Payment dates actually recorded on an expense (one per contribution), oldest first, deduplicated.
function paymentDates(expense) {
  return [...new Set(activeFundings(expense).map((f) => f.paid_at).filter(Boolean))].sort();
}

export function paymentDateLabel(expense) {
  const dates = paymentDates(expense);
  if (!dates.length) return "Non payée";
  if (dates.length === 1) return formatDay(dates[0]);
  return `${formatDay(dates[0])} → ${formatDay(dates[dates.length - 1])}`;
}

export function apiError(e) {
  return errorMessage(e, "Une erreur est survenue. Réessayez dans quelques instants.");
}

// Receipts: the same limits the server enforces (backend/finance/attachments.py).
export const RECEIPT_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
export const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
export function receiptProblem(file) {
  if (!RECEIPT_ACCEPT.split(",").includes(file.type)) return "Format non accepté : PDF ou image (JPEG, PNG, WebP).";
  if (file.size > RECEIPT_MAX_BYTES) return "Fichier trop lourd : 10 Mo maximum.";
  return null;
}

// Serial entry (« Justificatifs à saisir »): the piece to open after `currentId`, in list order, skipping it;
// wraps around to the first one still waiting. null when nothing else is waiting.
export function nextPieceId(items, currentId) {
  const ids = (items || []).map((i) => i.id).filter((id) => id !== currentId);
  if (!ids.length) return null;
  const index = (items || []).findIndex((i) => i.id === currentId);
  if (index < 0) return ids[0];
  const after = (items || []).slice(index + 1).find((i) => i.id !== currentId);
  return after ? after.id : ids[0];
}

// Contributions / exports filters → query params (empty values left out, so the server applies no filter).
export function reportParams({ dateFrom, dateTo, currency, status, preconstitution } = {}) {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (currency && currency !== "all") params.currency = currency;
  if (status && status !== "all") params.status = status;
  if (preconstitution) params.preconstitution = "true";
  return params;
}

export const PAYER_TYPE_LABELS = { collaborator: "Collaborateur", company: "Entreprise", third_party: "Tiers" };

// What becomes of an advance, decided collectively and recorded on each payment.
// How Jamora paid an advance back.
export const REIMBURSEMENT_METHODS = { virement: "Virement", wave: "Wave", orange_money: "Orange Money", especes: "Espèces", cheque: "Chèque", autre: "Autre" };

// Balances may be zero ("0.00"), which toMinor rejects as an amount to enter.
export function formatBalance(text, currency) {
  return formatMinor(toMinor(text, currency) ?? 0, currency);
}

// Recurring subscriptions.
export const FREQUENCY_LABELS = { monthly: "Mensuel", quarterly: "Trimestriel", yearly: "Annuel" };
export const SUBSCRIPTION_STATUS_LABELS = { active: "Actif", paused: "En pause", ended: "Arrêté" };

export const DISPOSITIONS = ["a_decider", "a_rembourser", "apport", "abandon"];
export const DISPOSITION_LABELS = { a_decider: "À décider", a_rembourser: "À rembourser", apport: "Apport", abandon: "Abandon" };
export const DISPOSITION_HINTS = {
  a_decider: "Aucune décision prise pour l'instant.",
  a_rembourser: "Jamora remboursera cette somme à la personne.",
  apport: "La somme est comptée comme un apport à la société.",
  abandon: "La personne renonce à être remboursée.",
};

// "{XOF: "606000", EUR: "20.00"}" → ["606 000 F CFA", "20,00 €"], in a stable order.
export const formatTotals = (totals) => Object.entries(totals || {}).sort(([a], [b]) => a.localeCompare(b)).map(([c, v]) => formatAmount(v, c));
